import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { slot: "footer" }
const _hoisted_4 = ["variant", "disabled"]
const _hoisted_5 = ["disabled"]

import { computed, ref, reactive, watch, nextTick } from 'vue';
import { useStore } from 'vuex';

import { epinioExceptionToErrorsArray } from '../../utils/errors';
import Banner from '@components/Banner/Banner.vue';
import AppSource from './AppSource.vue';
import AppInfo from './AppInfo.vue';
import AppConfiguration from './AppConfiguration.vue';
import AppProgress from './AppProgress.vue';
import { EpinioAppInfo, EpinioAppBindings, EpinioAppSource, EPINIO_TYPES } from '../../types';
import Tabs from './Tabs.vue';
import { allHash } from '@shell/utils/promise';
import EpinioApplicationModel from 'models/applications';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit'>('create');
// Model instance, used only for API calls
const serviceModel = ref<any>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const loading = ref(true);
const value = ref<any>(null);
const source = ref<EpinioAppSource>();
const bindings = ref<EpinioAppBindings>();
const appChart = reactive({ chartsList: undefined as any, selectedChart: undefined });
const epinioInfo = ref<any>(null);
const originalModel = ref<any>(null);

const snapshot = ref<string | null>(null);

const saving = ref(false);
const errors = ref<string[]>([]);
const activeTab = ref<string | number>('source')
const tabs = ref([
  { id: 'source', label: 'Source', completed: false, valid: false, disabled: false },
  { id: 'details', label: 'Details', completed: false, valid: false, disabled: true },
  { id: 'bindings', label: 'Bindings', completed: false, valid: true, disabled: true },
])

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const nextTab = computed(() => {
  const idx = tabs.value.findIndex(t => t.id === activeTab.value)
  return tabs.value[idx + 1]?.id
})

const prevTab = computed(() => {
  const idx = tabs.value.findIndex(t => t.id === activeTab.value)
  return tabs.value[idx - 1]?.id
})

const isDirty = computed(() => {
  if (!snapshot.value || !value.value) return false
  return takeSnapshot() !== snapshot.value
});

const isSourceDirty = computed(() => {
  if (!snapshot.value) return false;
  
  const snapshotSource = JSON.parse(snapshot.value).source;
  const currentSource = JSON.parse(takeSnapshot()).source;
  
  return JSON.stringify(snapshotSource) !== JSON.stringify(currentSource);
});

const showDiscardConfirm = ref(false);

function takeSnapshot() {
  return JSON.stringify({
    source: {
      ...source.value,
      git: {
        ...source.value?.git,
        sourceData: undefined, // ignore dynamic data
      },
    },
    bindings: bindings.value,
    meta: value.value?.meta,
    configuration: value.value?.configuration,
  });
}


async function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  loading.value = true;
  showModal.value = true;  // open modal first so user sees loading state

  tabs.value.push({ id: 'progress', label: 'Progress', completed: false, valid: true, disabled: true });

  const hash = await allHash({
    ns: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE }),
    charts: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS }),
    info: store.dispatch('epinio/info'),
  });

  epinioInfo.value = hash.info;
  appChart.chartsList = hash.charts;
  originalModel.value = await store.dispatch('epinio/create', { type: EPINIO_TYPES.APP });
  value.value = await store.dispatch('epinio/clone', { resource: originalModel.value });

  loading.value = false;

  await nextTick();
  snapshot.value = takeSnapshot();
}

async function openEdit(row: EpinioApplicationModel, commit?: string) {
  errors.value = [];
  modalMode.value = 'edit';
  loading.value = true;
  showModal.value = true;  // open modal first so user sees loading state

  const hash = await allHash({
    ns: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE }),
    charts: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS }),
    info: store.dispatch('epinio/info'),
  });

  epinioInfo.value = hash.info;
  appChart.chartsList = hash.charts;
  appChart.selectedChart = row.configuration?.appchart;
  originalModel.value = row;
  value.value = await store.dispatch('epinio/clone', { resource: originalModel.value });

  source.value = row.appSource;

  tabs.value.forEach(tab => {
    if (tab.id !== 'progress') tab.disabled = false;
    tab.valid = true;
  });

  if (!commit) loading.value = false;

  await nextTick();
  snapshot.value = takeSnapshot();

  // if opened from a specific commit, update source
  if (commit) {
    const newSource = {
      ...source.value,
      git: {
        ...source.value?.git,
        commit: commit,
      },
    };
    updateSource(newSource);
    loading.value = false;
  }
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // data
  value.value = null;
  originalModel.value = null;
  source.value = undefined;
  bindings.value = undefined;
  epinioInfo.value = null;
  appChart.chartsList = undefined;
  appChart.selectedChart = undefined;

  // ui state
  activeTab.value = 'source';
  tabs.value = tabs.value.filter(t => t.id !== 'progress') // remove progress tab added during create
  tabs.value.forEach((tab, i) => {
    tab.completed = false;
    tab.disabled = i !== 0;
  });
  errors.value = [];
  saving.value = false;
  loading.value = true;  // reset to true so next open shows spinner while fetching
  showDiscardConfirm.value = false;
  showModal.value = false;
  snapshot.value = null;

  // modal mode back to default
  modalMode.value = 'create';
  serviceModel.value = null;
}

// when namepace changes, remove bindings
watch(() => value.value?.meta.namespace, () => {
  bindings.value = { configurations: [], services: [] };
  set(value.value.configuration, { configurations: [] });
});

watch(() => isSourceDirty.value, () => {
  if (modalMode.value !== 'edit') {
    return;
  }
  if (isSourceDirty.value) {
    // add the progress tab if it doesn't exist (in case user goes back to source tab after completing it)
    if (!tabs.value.find(t => t.id === 'progress')) {
      tabs.value.push({ id: 'progress', label: 'Progress', completed: false, valid: true, disabled: true });
    }
  } else {
    // remove the progress tab if source is back to original
    tabs.value = tabs.value.filter(t => t.id !== 'progress');
  }
});

function set(obj: Record<string, any>, changes: Record<string, any>) {
  Object.entries(changes).forEach(([key, val]) => {
    obj[key] = val;
  });
}

function updateInfo(changes: EpinioAppInfo) {
  value.value.meta ||= {};
  value.value.configuration ||= {};
  set(value.value.meta, changes.meta);
  set(value.value.configuration, { settings: appChart.settings });
  set(value.value.configuration, changes.configuration);
}

function updateSource(changes: EpinioAppSource) {
  source.value = {};
  const { appChart: chartId, ...cleanChanges } = changes;

  const prevChartId = appChart.selectedChart;
  appChart.selectedChart = chartId;
  value.value.configuration ||= {};

  if (chartId) {
    set(value.value.configuration, { appchart: chartId });
  }

  const chartChanged = chartId !== prevChartId;
  if (!isEdit.value || chartChanged) {
    value.value.configuration.settings = undefined;

    if (chartId) {
      const chart = appChart.chartsList?.find((c: any) => c.id === chartId);

      if (chart?.settings) {
        const customSettings = Object.keys(chart.settings).reduce((acc, key) => {
          acc[key] = '';
          return acc;
        }, {} as Record<string, any>);

        set(value.value.configuration, { settings: customSettings });
        set(value.value, { chart });
      }
    }
  }

  set(source.value, cleanChanges);
}

function updateManifestConfigurations(configs: string[]) {
  set(value.value.configuration, { configurations: configs });
}

function updateConfigurations(changes: EpinioAppBindings) {
  bindings.value = {};
  set(bindings.value, changes);
  set(value.value.configuration, { configurations: changes.configurations });
}

async function onSubmit() {
  if (saving.value) return;
  saving.value = true;
  errors.value = [];

  try {
    if (isEdit.value) {
      // Always save metadata/config changes
      await value.value.update({ restart: !!value.value.canRestartAfterConfigSave });
      await value.value.updateConfigurations(
        originalModel.value.baseConfigurationsNames || [],
        bindings.value?.configurations || [],
      );
      await value.value.updateServices(
        originalModel.value.services || [],
        bindings.value?.services || [],
      );

      if (isSourceDirty.value) {
        // Source changed — need full redeploy pipeline
        saving.value = false;
        completeTab('bindings', 'progress'); // progress tab handles the rest
      } else {
        await value.value.forceFetch();
        closeModal();
      }
    } else {
      completeTab('bindings', 'progress');
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
  } finally {
    if (!isEdit.value) saving.value = false;
  }
}

function completeTab(tabId: string | number, nextTabId: string | number) {
  const tab = tabs.value.find((t) => t.id === tabId)
  const next = tabs.value.find((t) => t.id === nextTabId)
  if (tab && !isEdit.value)  tab.completed = true
  if (next) next.disabled = false
  // if moving to the last tab, disable all previous tabs to prevent jumping back and forth during deploy progress
  if (nextTabId === 'progress') {
    tabs.value.forEach(t => {
      if (t.id !== 'progress') t.disabled = true
    })
  }
  activeTab.value = nextTabId
}

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  const _component_Loading = _resolveComponent("Loading")!

  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    dismissible: false,
    title: (isEdit.value || isView.value) ? value.value?.meta?.name : 'Application',
    subtitle: (isEdit.value || isView.value) ? (value.value?.stateDisplay || '') : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      (loading.value)
        ? (_openBlock(), _createBlock(_component_Loading, { key: 0 }))
        : (_openBlock(), _createBlock(Tabs, {
            key: 1,
            modelValue: activeTab.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((activeTab).value = $event)),
            tabs: tabs.value
          }, {
            source: _withCtx(() => [
              _createVNode(AppSource, {
                application: value.value,
                source: source.value,
                mode: modalMode.value,
                info: epinioInfo.value,
                onChange: updateSource,
                onChangeAppInfo: updateInfo,
                onChangeAppConfig: updateManifestConfigurations,
                onValid: _cache[0] || (_cache[0] = (val) => {
              if (!isEdit.value)tabs.value[0].completed = val;
              tabs.value[0].valid = val;
              tabs.value[1].disabled = !val;
            })
              }, null, 8, ["application", "source", "mode", "info"])
            ]),
            details: _withCtx(({ tab }) => [
              _createVNode(AppInfo, {
                application: value.value,
                source: source.value,
                mode: modalMode.value,
                active: activeTab.value === tab.id,
                onChange: updateInfo,
                onValid: _cache[1] || (_cache[1] = (val) => {
              if (!isEdit.value) tabs.value[1].completed = val;
              tabs.value[1].valid = val;
              tabs.value[2].disabled = !val;
              // also disable final tab since the third tab has no required fields
              if (tabs.value[3] && !isEdit.value) tabs.value[3].disabled = !val;
            })
              }, null, 8, ["application", "source", "mode", "active"])
            ]),
            bindings: _withCtx(() => [
              _createVNode(AppConfiguration, {
                application: value.value,
                "initial-application": originalModel.value,
                mode: modalMode.value,
                bindings: bindings.value,
                onChange: updateConfigurations
              }, null, 8, ["application", "initial-application", "mode", "bindings"])
            ]),
            progress: _withCtx(({ tab }) => [
              _createVNode(AppProgress, {
                application: value.value,
                source: source.value,
                bindings: bindings.value,
                mode: modalMode.value,
                tab: tab,
                active: activeTab.value === tab.id
              }, null, 8, ["application", "source", "bindings", "mode", "tab", "active"])
            ]),
            _: 1,
            __: [6,7,8]
          }, 8, ["modelValue", "tabs"])),
      _cache[9] || (_cache[9] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[18] || (_cache[18] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[10] || (_cache[10] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[3] || (_cache[3] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[11] || (_cache[11] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[12] || (_cache[12] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[13] || (_cache[13] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              (activeTab.value !== 'progress')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 0,
                    variant: "secondary",
                    class: "mr-10",
                    onButtonClick: handleModalClose
                  }, "\n          Cancel\n        ", 32))
                : _createCommentVNode("", true),
              _cache[14] || (_cache[14] = _createTextVNode()),
              (!!prevTab.value && activeTab.value !== 'progress')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 1,
                    variant: "secondary",
                    class: "mr-10",
                    onButtonClick: _cache[4] || (_cache[4] = ($event: any) => (activeTab.value = prevTab.value))
                  }, "\n          Previous\n        ", 32))
                : _createCommentVNode("", true),
              _cache[15] || (_cache[15] = _createTextVNode()),
              (nextTab.value && activeTab.value !== 'progress')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 2,
                    variant: !isEdit.value ? 'primary' : 'secondary',
                    class: "mr-10",
                    disabled: tabs.value.find(t => t.id === nextTab.value)?.disabled,
                    onButtonClick: _cache[5] || (_cache[5] = ($event: any) => (completeTab(activeTab.value, nextTab.value)))
                  }, "\n          Next\n        ", 40, _hoisted_4))
                : _createCommentVNode("", true),
              _cache[16] || (_cache[16] = _createTextVNode()),
              (isEdit.value && activeTab.value !== 'progress')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 3,
                    variant: "primary",
                    disabled: !isDirty.value || saving.value || tabs.value.some(t => !t.valid),
                    onButtonClick: onSubmit
                  }, _toDisplayString(saving.value ? 'Saving...' : _unref(t)('generic.save')), 41, _hoisted_5))
                : _createCommentVNode("", true),
              _cache[17] || (_cache[17] = _createTextVNode()),
              (activeTab.value === 'progress')
                ? (_openBlock(), _createElementBlock("trailhand-button", {
                    key: 4,
                    variant: "primary",
                    disabled: false,
                    onButtonClick: closeModal
                  }, "\n          Finish\n        ", 32))
                : _createCommentVNode("", true)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})